
import React from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  Shield, 
  MessageSquare, 
  Calendar, 
  Gift 
} from "lucide-react";

interface FeatureProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const Feature = ({ icon, title, description }: FeatureProps) => {
  return (
    <div className="feature-card">
      <div className="bg-legatia-yellow/20 p-3 rounded-full w-14 h-14 flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="font-bold text-lg mb-2">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
};

const FeaturesSection = () => {
  const { language } = useLanguage();
  
  const content = {
    fr: {
      title: "Un cadeau qui grandit pour les moments qui comptent",
      features: [
        {
          title: "Sûr et sécurisé",
          description: "Plateforme suisse de sécurité et d'investissement."
        },
        {
          title: "Messages personnalisés",
          description: "Vos mots pour accompagner chaque moment important de leur vie."
        },
        {
          title: "Moments de déblocage",
          description: "Choisissez quand votre cadeau sera disponible: études, logement, mariage..."
        },
        {
          title: "Contributions évolutives",
          description: "Enrichissez le cadeau au fil du temps pour les moments clés."
        }
      ]
    },
    en: {
      title: "A Gift That Grows for Moments That Matter",
      features: [
        {
          title: "Safe & Secure",
          description: "Swiss security and investment platforms."
        },
        {
          title: "Personalized Messages",
          description: "Your words to accompany each important moment in their life."
        },
        {
          title: "Unlocking Moments",
          description: "Choose when your gift will be available: education, housing, wedding..."
        },
        {
          title: "Growing Contributions",
          description: "Enrich the gift over time for key moments."
        }
      ]
    },
    de: {
      title: "Ein Geschenk, das für wichtige Momente wächst",
      features: [
        {
          title: "Sicher & Geschützt",
          description: "Schweizer Sicherheits- und Investmentplattformen."
        },
        {
          title: "Personalisierte Nachrichten",
          description: "Ihre Worte begleiten jeden wichtigen Moment in ihrem Leben."
        },
        {
          title: "Freischaltungsmomente",
          description: "Wählen Sie, wann Ihr Geschenk verfügbar sein wird: Bildung, Wohnen, Hochzeit..."
        },
        {
          title: "Wachsende Beiträge",
          description: "Bereichern Sie das Geschenk im Laufe der Zeit für wichtige Momente."
        }
      ]
    }
  };
  
  const textContent = content[language];
  
  const icons = [
    <Shield className="h-6 w-6 text-legatia-orange" />,
    <MessageSquare className="h-6 w-6 text-legatia-orange" />,
    <Calendar className="h-6 w-6 text-legatia-orange" />,
    <Gift className="h-6 w-6 text-legatia-orange" />
  ];

  return (
    <section className="py-20 px-4 bg-gradient-to-b from-legatia-light/50 to-white">
      <div className="container mx-auto max-w-6xl">
        <h2 className="section-title text-center mb-16">
          {textContent.title}
        </h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {textContent.features.map((feature, index) => (
            <Feature
              key={index}
              icon={icons[index]}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
